// Meta (Facebook) tracking helpers for the Berleto headless storefront.
//
// Why this exists: the checkout is Shopify-hosted, so the Purchase event is sent
// SERVER-SIDE from the Shopify order webhook (see api/meta-purchase.js). For Meta
// to attribute that purchase to the ad click, we must capture the click id (_fbc)
// and browser id (_fbp) here on berleto.com and pass them into the cart so they
// land on the order. These helpers do that, plus fire the funnel Pixel events.

const PIXEL_ID = '2162361461282071';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match.pop()) : null;
}

function setCookie(name, value, days = 90) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

// Call once on app load. If the visitor arrived from a Meta ad (?fbclid=...),
// make sure the _fbc cookie exists even before the Pixel writes it.
export function initMetaClickId() {
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid && !getCookie('_fbc')) {
      setCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`);
    }
  } catch (e) {
    /* no-op */
  }
}

export const getFbp = () => getCookie('_fbp') || '';
export const getFbc = () => getCookie('_fbc') || '';

export function newEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'evt-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

// Tracking data attached to the Shopify cart as attributes. They arrive on the
// order as note_attributes, where api/meta-purchase.js reads them for the CAPI call.
// Shopify hides keys prefixed with "_" from the admin UI but still sends them in the webhook.
export function buildCheckoutTrackingAttributes(eventId) {
  const attrs = [
    { key: '_fbp', value: getFbp() },
    { key: '_fbc', value: getFbc() },
    { key: '_fb_event_id', value: eventId },
    { key: '_fb_source_url', value: typeof window !== 'undefined' ? window.location.href : '' },
    { key: '_fb_client_ua', value: typeof navigator !== 'undefined' ? navigator.userAgent : '' },
  ];
  // Shopify rejects empty attribute values — keep only the ones we actually have.
  return attrs.filter((a) => a.value);
}

function fbqTrack(event, data, eventId) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  const clean = {};
  Object.keys(data || {}).forEach((k) => {
    if (data[k] !== undefined && data[k] !== null) clean[k] = data[k];
  });
  window.fbq('track', event, clean, eventId ? { eventID: eventId } : undefined);
}

export function trackViewContent({ id, name, price, currency = 'USD' } = {}) {
  fbqTrack('ViewContent', {
    content_ids: id ? [String(id)] : undefined,
    content_name: name,
    content_type: 'product',
    value: price != null ? Number(price) : undefined,
    currency,
  }, newEventId());
}

export function trackAddToCart({ id, name, price, quantity = 1, currency = 'USD' } = {}) {
  fbqTrack('AddToCart', {
    content_ids: id ? [String(id)] : undefined,
    content_name: name,
    content_type: 'product',
    value: price != null ? Number(price) * quantity : undefined,
    currency,
  }, newEventId());
}

export function trackInitiateCheckout({ value, numItems, contentIds, currency = 'USD' } = {}, eventId) {
  fbqTrack('InitiateCheckout', {
    value: value != null ? Number(value) : undefined,
    num_items: numItems,
    content_ids: contentIds,
    content_type: 'product',
    currency,
  }, eventId || newEventId());
}

export { PIXEL_ID };
